import {Locale, LocaleTag} from '@packages/systems/localization-types';
import type {JSONNode} from '@packages/shared/types/JSONNode';
import {Expressions, matchExpression} from '@packages/systems/wfdl';
import {constant, concat} from '@packages/utilities/fp/utils';

import {SymbolsLocalizationData} from '../types';
import {Expression} from '@packages/systems/wfdl-ast';
import {RawFileType} from '@packages/systems/core/records/FileRecord';
import type {ObjectId} from 'bson';

type SymbolsLocalization = {
  localeId: ObjectId | string;
  symbols: {
    [nodeId: string]: JSONNode;
  };
};

type LocaleISOCodeIndex = Record<string, LocaleTag>;

/**
 * Helper utility to traverse and map the provided symbols localizations data.
 * Maps each locale.symbols list using the provided matchLocale and each symbol
 * in each list using the provided matchSymbol.
 */
export function traverseSymbolsLocalizationsData({
  symbolsLocalizations,
  matchSymbol,
  matchLocale,
}: {
  symbolsLocalizations: SymbolsLocalizationData;
  matchSymbol?: (locale: LocaleTag, symbol: JSONNode) => JSONNode;
  matchLocale?: (
    locale: LocaleTag,
    symbols: SymbolsLocalizationData[LocaleTag]
  ) => SymbolsLocalizationData[LocaleTag];
}): SymbolsLocalizationData {
  if (!matchSymbol && !matchLocale) {
    return symbolsLocalizations;
  }

  return Object.fromEntries(
    Object.entries(symbolsLocalizations).map(([locale, symbols]) => {
      const mappedSymbols = matchLocale
        ? matchLocale(locale, symbols)
        : symbols;
      return [
        locale,
        matchSymbol
          ? Object.fromEntries(
              Object.entries(mappedSymbols).map(([symbolId, symbol]) => {
                const mappedSymbol = matchSymbol(locale, symbol);
                return [symbolId, mappedSymbol];
              })
            )
          : mappedSymbols,
      ];
    })
  );
}

/**
 * Transforms a list of Locales into a LocaleId -> LocaleTag index.
 */
export function buildLocalesISOCodeIndex(
  locales: Array<Locale>
): LocaleISOCodeIndex {
  return locales.reduce((acc, locale) => {
    acc[locale._id.toString()] = locale.tag;
    return acc;
  }, {} as LocaleISOCodeIndex);
}

/**
 * Given a list of SymbolsLocalization and a LocaleISOCodeIndex, this function
 * will merge the two into a single index object of
 * LocaleTag -> SymbolsLocalization.symbols
 */
export function parseRawSymbolsLocalizationData(
  rawSymbolsLocalization: Array<SymbolsLocalization>,
  localesIndex: LocaleISOCodeIndex
): SymbolsLocalizationData {
  const symbolsLocalization: SymbolsLocalizationData = {};

  rawSymbolsLocalization.forEach((symbolLocalization) => {
    const tag = localesIndex[symbolLocalization.localeId.toString()];
    if (!tag) {
      return;
    }
    symbolsLocalization[tag] = symbolLocalization.symbols;
  });

  return symbolsLocalization;
}

/**
 * Given a list of RawFileType assets and a LocaleISOCodeIndex, this function
 * will go through each asset and parse and update its localizedSettings into
 * LocaleTag -> localizedSettings
 */
export function parseRawAssetsLocalizationSettings(
  assets: Array<RawFileType>,
  localesIndex: LocaleISOCodeIndex
) {
  return assets.map((asset) => {
    if (!asset.localizedSettings) {
      return asset;
    }
    const updatedLocalizedSettings = Object.keys(
      asset.localizedSettings
    ).reduce<
      Record<LocaleTag, NonNullable<RawFileType['localizedSettings']>[string]>
    >((acc, key) => {
      const tag = localesIndex[key];
      const fileLocaleSettings =
        asset.localizedSettings && asset.localizedSettings[key];
      if (!tag || !fileLocaleSettings) {
        return acc;
      }
      acc[tag] = fileLocaleSettings;
      return acc;
    }, {});
    return {
      ...asset,
      localizedSettings: updatedLocalizedSettings,
    };
  });
}

const getElementIdsFromExpression = Expressions.everything(concat)(
  matchExpression({
    Element: ({id}) => {
      return Expressions.resume([id]);
    },
    _: constant(Expressions.resume([])),
  })
);

/**
 * Returns a subset of the symbols localizations that match some Element ID
 * present in the given render Expression. It performs this filtering by iterating
 * over all symbols for all locales present in the given symbolsLocalizations
 * index.
 */
export function getAllSymbolsLocalizationsToInclude(
  render: Expression,
  symbolsLocalizations: SymbolsLocalizationData
): SymbolsLocalizationData {
  const elementIds = getElementIdsFromExpression(render);
  const symbolsLocalizationsToInclude: SymbolsLocalizationData = {};

  traverseSymbolsLocalizationsData({
    symbolsLocalizations,
    matchSymbol(locale, symbol) {
      if (elementIds.includes(symbol._id)) {
        symbolsLocalizationsToInclude[locale] = {
          ...(symbolsLocalizationsToInclude[locale] ?? {}),
          [symbol._id]: symbol,
        };
      }
      return symbol;
    },
  });

  return symbolsLocalizationsToInclude;
}

export const forTestSuite = {
  parseRawAssetsLocalizationSettings,
  traverseSymbolsLocalizationsData,
};
